import Model, { attr, hasMany } from '@ember-data/model';
import { cached } from '@glimmer/tracking';
import type { ModelRegistry } from 'ember-data/types/registries/model';

export enum AlertStatus {
  pending = 'pending',
  resolved = 'resolved',
}

export enum AlertType {
  airbnbUserSuspension = 'airbnb_official_connection_user_suspension',
  airbnbOfficialAlterationRequestCreated = 'airbnb_official_alteration_request_created',
  airbnbOfficialAlterationRequestDeclined = 'airbnb_official_alteration_request_declined',
  airbnbOfficialAlterationRequestAccepted = 'airbnb_official_alteration_request_accepted',
  bookingDotComExtraChargesError = 'booking_dot_com_extra_charges_error',
  airbnbOfficialConnectionAuthorizationRevoked = 'airbnb_official_connection_authorization_revoked',
  bookingDotComAccessRevoked = 'booking_dot_com_access_revoked',
  bookingDotComAccessNeverGranted = 'booking_dot_com_access_never_granted',
  vrboPropertyInactive = 'vrbo_property_inactive',
  vrboAdvertiserUnnamed = 'homeaway_advertiser_unnamed',
  vrboContentIncomplete = 'homeaway_content_incomplete',
  vrboListingsDelistedWhileLapsed = 'homeaway_listings_delisted_while_lapsed',
  bookingDotComRatePlanChanged = 'booking_dot_com_rate_plan_changed',
  bookingDotComRatePlanNeedsAttention = 'booking_dot_com_rate_plan_needs_attention',
  bookingDotComAvailabilityRejected = 'booking_dot_com_availability_rejected',
  bookingDotComListingNotActive = 'booking_dot_com_listing_not_active',
  bookingDotComLosDiscountsPaused = 'booking_dot_com_los_discounts_paused',
}

export default class AlertModel extends Model {
  @attr('string') title!: string;
  @attr('string') description!: string;
  @attr('string') status!: AlertStatus;
  @attr('string') type!: AlertType;
  @attr('date') createdAt!: Date;

  @hasMany('airbnb-official-connection', { async: false, inverse: null })
  airbnbOfficialConnections?: ModelRegistry['airbnb-official-connection'][];

  @hasMany('channel-link', { async: false, inverse: null })
  channelLinks?: ModelRegistry['channel-link'][];

  @hasMany('channel-connection', { async: false, inverse: null })
  channelConnections?: ModelRegistry['channel-connection'][];

  @hasMany('property', { async: false, inverse: null })
  properties?: ModelRegistry['property'][];

  @hasMany('booking', { async: false, inverse: null })
  bookings?: ModelRegistry['booking'][];

  @cached
  get isResolved(): boolean {
    return this.status === AlertStatus.resolved;
  }

  @cached
  get isSubmitting(): boolean {
    return !!this.isSaving;
  }

  @cached
  get isAirbnbUserSuspension(): boolean {
    return this.type === AlertType.airbnbUserSuspension;
  }

  @cached
  get isBookingDotComExtraChargesError(): boolean {
    return this.type === AlertType.bookingDotComExtraChargesError;
  }

  @cached
  get isAirbnbOfficialConnectionAuthorizationRevoked(): boolean {
    return this.type === AlertType.airbnbOfficialConnectionAuthorizationRevoked;
  }

  @cached
  get isBookingDotComAccessRevoked(): boolean {
    return this.type === AlertType.bookingDotComAccessRevoked;
  }

  @cached
  get isBookingDotComAccessNeverGranted(): boolean {
    return this.type === AlertType.bookingDotComAccessNeverGranted;
  }

  @cached
  get isVrboPropertyInactive(): boolean {
    return this.type === AlertType.vrboPropertyInactive;
  }

  @cached
  get isBookingDotComListingAlert(): boolean {
    return [
      AlertType.bookingDotComRatePlanChanged,
      AlertType.bookingDotComRatePlanNeedsAttention,
      AlertType.bookingDotComAvailabilityRejected,
      AlertType.bookingDotComListingNotActive,
    ].includes(this.type);
  }

  @cached
  get isBookingDotComLosDiscountsPaused(): boolean {
    return this.type === AlertType.bookingDotComLosDiscountsPaused;
  }

  @cached
  get isVrboAdvertiserUnnamed(): boolean {
    return this.type === AlertType.vrboAdvertiserUnnamed;
  }

  @cached
  get isVrboContentIncomplete(): boolean {
    return this.type === AlertType.vrboContentIncomplete;
  }

  @cached
  get isVrboListingsDelistedWhileLapsed(): boolean {
    return this.type === AlertType.vrboListingsDelistedWhileLapsed;
  }

  @cached
  get isVrboAlert(): boolean {
    return (
      this.isVrboAdvertiserUnnamed ||
      this.isVrboContentIncomplete ||
      this.isVrboListingsDelistedWhileLapsed
    );
  }

  @cached
  get vrboConnections(): ModelRegistry['channel-connection'][] {
    return this.isVrboAlert ? (this.channelConnections ?? []) : [];
  }

  @cached
  get isAirbnbAlterationAlert(): boolean {
    return [
      AlertType.airbnbOfficialAlterationRequestCreated,
      AlertType.airbnbOfficialAlterationRequestDeclined,
      AlertType.airbnbOfficialAlterationRequestAccepted,
    ].includes(this.type);
  }

  @cached
  get isActionable(): boolean {
    if (this.isAirbnbUserSuspension) {
      return true;
    } else if (this.isAirbnbAlterationAlert) {
      return !!this.bookings?.[0];
    } else if (this.isBookingDotComExtraChargesError) {
      return !!this.firstProperty;
    } else if (this.isAirbnbOfficialConnectionAuthorizationRevoked) {
      return !!this.firstAirbnbOfficialConnection;
    } else if (this.isBookingDotComAccessRevoked) {
      return !!this.firstChannelLink;
    } else if (this.isBookingDotComAccessNeverGranted) {
      // The connection was never completed, so there's no channel connection to
      // expand — the CTA always routes to the Booking.com connect page.
      return true;
    } else if (
      this.isVrboPropertyInactive ||
      this.isVrboAdvertiserUnnamed ||
      this.isVrboContentIncomplete
    ) {
      return !!this.firstProperty;
    } else if (this.isVrboListingsDelistedWhileLapsed) {
      return true;
    } else if (this.isBookingDotComListingAlert) {
      return !!this.firstProperty;
    } else if (this.isBookingDotComLosDiscountsPaused) {
      return true;
    }

    return false;
  }

  @cached
  get bookingForType(): ModelRegistry['booking'] | undefined {
    if (this.isAirbnbAlterationAlert) {
      return this.bookings?.[0];
    }
  }

  @cached
  get firstAirbnbOfficialConnection():
    | ModelRegistry['airbnb-official-connection']
    | undefined {
    return this.airbnbOfficialConnections?.[0];
  }

  @cached
  get firstChannelLink(): ModelRegistry['channel-link'] | undefined {
    return this.channelLinks?.[0];
  }

  @cached
  get firstProperty(): ModelRegistry['property'] | undefined {
    return this.properties?.[0];
  }
}

declare module 'ember-data/types/registries/model' {
  interface ModelRegistry {
    alert: AlertModel;
  }
}
